// SDK install (contracts.md:41,51). App-key registration returns
// `RegisterAppKeyResponse` (model/app-key.ts) - the FROZEN shape, `rawKey` shown once.
// `POST /v1/apps/:id/sdk-install` RECONCILED at Wave-3 integration against the real
// apps/api response (apps/api/src/onboarding/sdk-install.service.ts SdkInstallResult):
// `{recipe, appKeyId, writeKey?}` where `recipe` mirrors @nightcrew/sdk's
// SDK_INSTALL_RECIPES entry shape and `writeKey` is present ONLY when a key was freshly
// minted (returned once, never stored). Fixture-first this slice: no PR exists yet, so
// the screen derives its progress display from this shape (see SdkInstall.tsx).
import { z } from "zod";
import { AppId, RegisterAppKeyResponse } from "@nightcrew/shared";
import type { Result } from "@nightcrew/shared";
import { apiPost } from "./client.js";
import type { ClientError } from "./errors.js";

export const registerAppKey = (
  appId: AppId,
): Promise<Result<RegisterAppKeyResponse, ClientError>> =>
  apiPost(`/v1/apps/${appId}/app-keys`, RegisterAppKeyResponse, {});

/** The install recipe entry (mirrors @nightcrew/sdk SDK_INSTALL_RECIPES; parsed leniently). */
export const SdkInstallRecipeInfo = z.object({
  id: z.string(),
  stack: z.string(),
  title: z.string(),
  packageName: z.string(),
  envVars: z.array(z.object({ name: z.string(), description: z.string() })),
  files: z.array(z.object({ path: z.string(), description: z.string(), contents: z.string() })),
});
export type SdkInstallRecipeInfo = z.infer<typeof SdkInstallRecipeInfo>;

export const SdkInstallResponse = z.object({
  recipe: SdkInstallRecipeInfo,
  appKeyId: z.string(),
  writeKey: z.string().optional(),
});
export type SdkInstallResponse = z.infer<typeof SdkInstallResponse>;

export const materializeSdkInstall = (
  appId: AppId,
): Promise<Result<SdkInstallResponse, ClientError>> =>
  apiPost(`/v1/apps/${appId}/sdk-install`, SdkInstallResponse, {});
